import {
  brandOptions,
  organizations,
  suppliers,
  adminUnits,
  contactPersons,
} from './itemsFixtureData';

/**
 * DL-65642 (epic — dl-58221 part 1, "the newly sortable columns"). Real, persisted Items rows
 * that make the newly-sortable columns' ordering *falsifiable* against the MirageJS handler's
 * faithful server-side sort (`getSortValue` / `sortItems` in `items.ts`). They are REAL rows in
 * `itemsData` — not `apiTransform` GET overrides — because an `apiTransform` `data` override
 * replaces the response wholesale and bypasses `sortItems`, so it can only prove the fixture's
 * hardcoded order, never that the server actually sorted (see the epic's fixture note).
 *
 * Two of the columns cannot be proven from the generated base set on their own:
 *  - **Supplier contact** — every generated row has `supplierContactPerson: null`, so sorting the
 *    base data is unfalsifiable (all values empty). These three rows are the only ones with a
 *    non-null, DISTINCT supplier contact, seeded in a deliberately non-alphabetical order.
 *  - **Default quantity** — the generator only ever emits `1..15`, so there is no `0` (a value,
 *    not an absence) and no null to prove the `0`-vs-null distinction (Testmo 753711).
 *  - **Description** — seeded here with distinct values plus an explicit null so the null-ordering
 *    case (Testmo 753715) is deterministic rather than depending on faker's `maybe()`.
 *
 * Every name carries a unique `zzSortProbe <Group>` token so a spec isolates exactly one group
 * with `?search=<token>` (the mock's free-text name search), and — because the token sorts last
 * under the default name/ASC load — these rows never disturb page-1 order for other specs.
 */

let probeId = 943100;

const sortProbe = (name: string, overrides: Record<string, unknown>) => ({
  id: probeId++,
  name,
  userDefinedId: `IT-${probeId}`,
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-02-11T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 7,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
  ...overrides,
});

// Supplier contact — three distinct, non-alphabetically-seeded contacts (search: "zzSortProbe SupplierContact").
export const itemsSortingSupplierContactProbes = [
  sortProbe('zzSortProbe SupplierContact Alpha', {
    supplierContactPerson: { id: contactPersons[2].id, name: contactPersons[2].name },
  }),
  sortProbe('zzSortProbe SupplierContact Bravo', {
    supplierContactPerson: { id: contactPersons[0].id, name: contactPersons[0].name },
  }),
  sortProbe('zzSortProbe SupplierContact Charlie', {
    supplierContactPerson: { id: contactPersons[1].id, name: contactPersons[1].name },
  }),
];

// Unit / admin unit — three distinct, non-alphabetically-seeded units (search: "zzSortProbe Unit").
// Isolated to a probe set because the generated set only cycles a handful of units and the
// clear-option fixtures carry a null unit, so a full-table page cannot prove a falsifiable order.
export const itemsSortingUnitProbes = [
  sortProbe('zzSortProbe Unit Alpha', { adminUnit: adminUnits[2] }),
  sortProbe('zzSortProbe Unit Bravo', { adminUnit: adminUnits[0] }),
  sortProbe('zzSortProbe Unit Charlie', { adminUnit: adminUnits[3] }),
];

// Default quantity — 0 (a value), two positive values and an explicit null (search: "zzSortProbe Qty").
export const itemsSortingQuantityProbes = [
  sortProbe('zzSortProbe Qty Zero', { defaultQty: 0 }),
  sortProbe('zzSortProbe Qty Five', { defaultQty: 5 }),
  sortProbe('zzSortProbe Qty Twenty', { defaultQty: 20 }),
  sortProbe('zzSortProbe Qty Null', { defaultQty: null }),
];

// Description — distinct values plus an explicit null (search: "zzSortProbe Desc").
export const itemsSortingDescriptionProbes = [
  sortProbe('zzSortProbe Desc Mango', { description: 'Mango sorting probe' }),
  sortProbe('zzSortProbe Desc Apple', { description: 'Apple sorting probe' }),
  sortProbe('zzSortProbe Desc Zebra', { description: 'Zebra sorting probe' }),
  sortProbe('zzSortProbe Desc Null', { description: null }),
];

export const itemsSortingProbeRows = [
  ...itemsSortingSupplierContactProbes,
  ...itemsSortingUnitProbes,
  ...itemsSortingQuantityProbes,
  ...itemsSortingDescriptionProbes,
];
